/**
 * Aspect — Touch joystick visual + mobile loading progress + gyro toggle
 * --------------------------------------------------------------
 * DOM companions for the touch controls and asset streaming (engine modules
 * stay DOM-free):
 *
 *   - Renders the virtual joysticks (move + look) from "fp:joystick" /
 *     "fp:lookstick" events fired by FirstPersonControls.
 *   - Shows persistent left/right hint pads on coarse-pointer (phone) devices
 *     so walk + look controls are discoverable without a tutorial.
 *   - Gyro toggle requests DeviceOrientation permission (iOS) and enables
 *     immersive tilt-to-look.
 *   - Shows a small "Loading assets… n/m" chip driven by "assets:progress"
 *     events from the AssetRegistry while GLBs/textures stream in.
 */

import { isTouchUi } from "../device_profile.js";

function makeJoyEl(extraClass = "") {
  const el = document.createElement("div");
  el.className = `touch-joy hidden${extraClass ? ` ${extraClass}` : ""}`;
  el.innerHTML = `<div class="touch-joy__knob"></div>`;
  return el;
}

export class TouchJoystickUI {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {HTMLElement} mount overlay mount
   */
  constructor(app, mount) {
    this.app = app;
    this.touchUi = isTouchUi();
    this._walkActive = true;
    this._overhead = false;
    this._introDone = false;
    this._gyroOn = false;
    this._seated = false;
    this._moveActive = false;
    this._lookActive = false;

    this.base = makeJoyEl();
    mount.appendChild(this.base);
    this.knob = this.base.querySelector(".touch-joy__knob");

    this.lookBase = makeJoyEl("touch-joy--look");
    mount.appendChild(this.lookBase);
    this.lookKnob = this.lookBase.querySelector(".touch-joy__knob");

    this.hints = document.createElement("div");
    this.hints.className = "touch-hints hidden";
    this.hints.innerHTML = `
      <div class="touch-hint touch-hint--move" data-hint="walk-move" aria-hidden="true">
        <span class="touch-hint__ring"></span>
        <span class="touch-hint__label">Move</span>
      </div>
      <div class="touch-hint touch-hint--look" data-hint="walk-look" aria-hidden="true">
        <span class="touch-hint__ring"></span>
        <span class="touch-hint__label">Look</span>
      </div>
      <div class="touch-hint touch-hint--overhead-rotate" data-hint="oh-rotate" aria-hidden="true">
        <span class="touch-hint__ring"></span>
        <span class="touch-hint__label">Drag · rotate</span>
      </div>
      <div class="touch-hint touch-hint--overhead-zoom" data-hint="oh-zoom" aria-hidden="true">
        <span class="touch-hint__ring"></span>
        <span class="touch-hint__label">Pinch · zoom</span>
      </div>
    `;
    mount.appendChild(this.hints);

    this.livePill = document.createElement("div");
    this.livePill.className = "touch-live-pill";
    this.livePill.innerHTML = `<span class="touch-live-pill__dot" aria-hidden="true"></span><span>Live</span>`;
    mount.appendChild(this.livePill);

    this.gyroBtn = document.createElement("button");
    this.gyroBtn.type = "button";
    this.gyroBtn.className = "touch-gyro-btn";
    this.gyroBtn.textContent = "Gyro";
    this.gyroBtn.title = "Tilt phone to look around";
    this.gyroBtn.setAttribute("aria-pressed", "false");
    this.gyroBtn.addEventListener("pointerup", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.app.fire("fp:gyro-toggle", {});
    });
    const status = mount.querySelector(".hud-status");
    if (status?.parentNode) status.parentNode.insertBefore(this.gyroBtn, status);
    else mount.appendChild(this.gyroBtn);

    this.progress = document.createElement("div");
    this.progress.className = "hud-panel load-progress hidden";
    mount.appendChild(this.progress);
    this._hideTimer = null;

    this._onJoy = (j) => this._renderJoy(this.base, this.knob, j, "move");
    this._onLook = (j) => this._renderJoy(this.lookBase, this.lookKnob, j, "look");
    this._onProgress = (p) => this._renderProgress(p);
    this._onFpEnabled = ({ enabled }) => {
      this._walkActive = enabled !== false;
      this._syncHints();
    };
    this._onCamMode = ({ mode }) => {
      this._overhead = mode === "overhead";
      if (this._overhead) {
        this.base.classList.add("hidden");
        this.lookBase.classList.add("hidden");
        this._moveActive = false;
        this._lookActive = false;
      }
      this._syncHints();
    };
    this._onIntro = () => {
      this._introDone = true;
      this._syncHints();
    };
    this._onOrientation = () => {
      this.base.classList.add("hidden");
      this.lookBase.classList.add("hidden");
      this._moveActive = false;
      this._lookActive = false;
      this.hints.classList.remove("touch-hints--dim");
    };
    this._onGyro = ({ enabled, denied }) => {
      this._gyroOn = Boolean(enabled);
      this.gyroBtn.classList.toggle("touch-gyro-btn--on", this._gyroOn);
      this.gyroBtn.classList.toggle("touch-gyro-btn--denied", Boolean(denied));
      this.gyroBtn.setAttribute("aria-pressed", this._gyroOn ? "true" : "false");
      this.gyroBtn.textContent = denied ? "Gyro blocked" : this._gyroOn ? "Gyro On" : "Gyro";
    };
    this._onSit = ({ seated }) => {
      this._seated = Boolean(seated);
      this._syncHints();
    };

    app.on("fp:joystick", this._onJoy);
    app.on("fp:lookstick", this._onLook);
    app.on("assets:progress", this._onProgress);
    app.on("fp:enabled", this._onFpEnabled);
    app.on("cam:mode-changed", this._onCamMode);
    app.on("ui:intro-dismissed", this._onIntro);
    app.on("ui:orientation", this._onOrientation);
    app.on("fp:gyro", this._onGyro);
    app.on("sit:state", this._onSit);

    if (this.touchUi) document.body.classList.add("touch-ui");
    this._syncHints();
  }

  _syncHints() {
    const ready = this.touchUi && this._introDone;
    const walk = ready && this._walkActive && !this._overhead;
    const seated = walk && this._seated;
    const overhead = ready && this._overhead;
    this.hints.classList.toggle("hidden", !(walk || overhead));
    this.hints.classList.toggle("touch-hints--overhead", overhead);
    this.gyroBtn.classList.toggle("touch-gyro-btn--show", walk);
    for (const el of this.hints.querySelectorAll("[data-hint]")) {
      const id = el.getAttribute("data-hint");
      const show =
        (walk && !seated && (id === "walk-move" || id === "walk-look")) ||
        (seated && id === "walk-look") ||
        (overhead && (id === "oh-rotate" || id === "oh-zoom"));
      el.classList.toggle("hidden", !show);
    }
  }

  _renderJoy(base, knob, { active, x, y, dx, dy }, which) {
    base.classList.toggle("hidden", !active);
    if (which === "move") this._moveActive = Boolean(active);
    else this._lookActive = Boolean(active);
    this.hints.classList.toggle("touch-hints--dim", this._moveActive || this._lookActive);
    if (!active) return;
    base.style.left = `${x}px`;
    base.style.top = `${y}px`;
    knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  _renderProgress({ done, total }) {
    if (this._hideTimer) {
      window.clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
    if (total > 0 && done < total) {
      this.progress.textContent = `Loading assets… ${done}/${total}`;
      this.progress.classList.remove("hidden");
    } else {
      // Linger briefly so the final count is readable, then fade out.
      this.progress.textContent = total > 0 ? `Loaded ${total} assets` : "";
      this._hideTimer = window.setTimeout(() => this.progress.classList.add("hidden"), 900);
    }
  }

  destroy() {
    this.app.off("fp:joystick", this._onJoy);
    this.app.off("fp:lookstick", this._onLook);
    this.app.off("assets:progress", this._onProgress);
    this.app.off("fp:enabled", this._onFpEnabled);
    this.app.off("cam:mode-changed", this._onCamMode);
    this.app.off("ui:intro-dismissed", this._onIntro);
    this.app.off("ui:orientation", this._onOrientation);
    this.app.off("fp:gyro", this._onGyro);
    this.app.off("sit:state", this._onSit);
    if (this._hideTimer) window.clearTimeout(this._hideTimer);
    this.base.remove();
    this.lookBase.remove();
    this.hints.remove();
    this.livePill?.remove();
    this.gyroBtn?.remove();
    this.progress.remove();
  }
}

export default TouchJoystickUI;
